if (!customElements.get('faq-category-navigation')) {
  class FaqCategoryNavigation extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-faq-category-trigger]');
      this.value = this.querySelector('[data-faq-category-value]');
      this.menu = this.querySelector('[data-faq-category-menu]');
      this.options = Array.from(this.querySelectorAll('[data-faq-category-option]'));
      this.section = this.closest('.cc-faq');
      this.categories = this.section ? Array.from(this.section.querySelectorAll('[data-faq-category]')) : [];

      if (!this.trigger || !this.menu || !this.options.length || !this.categories.length) return;

      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleOptionClick = this.handleOptionClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);

      this.trigger.addEventListener('click', this.handleTriggerClick);
      this.options.forEach((option) => option.addEventListener('click', this.handleOptionClick));
      this.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('click', this.handleDocumentClick);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleScroll, { passive: true });
      this.handleScroll();
    }

    disconnectedCallback() {
      if (!this.trigger) return;
      this.trigger.removeEventListener('click', this.handleTriggerClick);
      this.options.forEach((option) => option.removeEventListener('click', this.handleOptionClick));
      this.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('click', this.handleDocumentClick);
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    getStickyTop() {
      const renderedTop = Number.parseFloat(window.getComputedStyle(this).top);
      return Number.isFinite(renderedTop) ? renderedTop : Number(this.dataset.stickyOffset || 0);
    }

    getScrollOffset() {
      return this.getStickyTop() + this.offsetHeight + 24;
    }

    openMenu(focusSelected = false) {
      this.menu.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');

      if (focusSelected) {
        (this.options.find((option) => option.getAttribute('aria-selected') === 'true') || this.options[0]).focus();
      }
    }

    closeMenu(returnFocus = false) {
      this.menu.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      if (returnFocus) this.trigger.focus();
    }

    handleTriggerClick() {
      if (this.menu.hidden) {
        this.openMenu(false);
      } else {
        this.closeMenu(false);
      }
    }

    handleOptionClick(event) {
      this.setActiveOption(event.currentTarget);
      this.closeMenu(true);
      this.navigateTo(event.currentTarget.dataset.value);
    }

    setActiveOption(activeOption) {
      if (!activeOption) return;
      this.options.forEach((option) => option.setAttribute('aria-selected', String(option === activeOption)));
      this.value.textContent = activeOption.textContent.trim();
    }

    navigateTo(id) {
      const target = document.getElementById(id);
      if (!target) return;

      const top = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
      window.scrollTo({
        top: Math.max(0, top),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    handleKeydown(event) {
      if (event.key === 'Tab' && !this.menu.hidden) {
        window.setTimeout(() => this.closeMenu(false));
        return;
      }

      if (event.key === 'Escape' && !this.menu.hidden) {
        event.preventDefault();
        this.closeMenu(true);
        return;
      }

      if (event.target === this.trigger && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.openMenu(true);
        return;
      }

      const currentIndex = this.options.indexOf(event.target);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;
      if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.options.length;
      if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.options.length) % this.options.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.options.length - 1;

      if (nextIndex !== currentIndex) {
        event.preventDefault();
        this.options[nextIndex].focus();
      }
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target) && !this.menu.hidden) this.closeMenu(false);
    }

    handleScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        const stickyTop = this.getStickyTop();
        const threshold = this.getScrollOffset() + 8;
        let active = this.categories[0];

        this.categories.forEach((category) => {
          if (category.getBoundingClientRect().top <= threshold) active = category;
        });

        const activeOption = this.options.find((option) => option.dataset.value === active.id);
        this.setActiveOption(activeOption);
        this.classList.toggle('is-stuck', this.getBoundingClientRect().top <= stickyTop + 1);
        this.frame = null;
      });
    }
  }

  customElements.define('faq-category-navigation', FaqCategoryNavigation);
}
